import React from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { validateSearchQuery } from '../../utils/searchValidation';

export const SEARCH_GAP_MESSAGE = "We don't have content for this yet.";
// A learner's search with no answer (N4 pt 2.1): one line that also says the gap is followed up (Searches with no
// answer, N5 pt 34). The app shows it now, the widget with its own change (PR #12). Add content's Try it keeps
// SEARCH_GAP_MESSAGE, since no follow-up comes from an author's check (K21.2 pt 34).
export const LEARNER_GAP_MESSAGE = "We don't have content for this yet and will follow up once it's added. Thanks.";
// The fourth search state (K15 pt 4): the search itself failed (time limit or provider error). It is never shown as
// the gap, and it comes with a Try again button. Removed 10 Oct 2026 (K15 pt 4): 'Unable to search content right now.
// Please try again.' with no button.
export const SEARCH_ERROR_MESSAGE = 'Search temporarily unavailable.';
const NO_COVERAGE_ANSWER = 'The content does not cover this topic.';

// headers: extra request headers, e.g. the embed widget's token (K13).
export const searchContent = async (query, { endpoint = '/api/contents/search', credentials = 'same-origin', params = {}, headers = {} } = {}) => {
  const validation = validateSearchQuery(query);
  if (!validation.isValid) {
    return { kind: 'validation', message: validation.message, severity: 'error' };
  }

  try {
    // Extra parameters, e.g. contentIds to limit the ingestion screen's Try it box to one batch.
    const extra = Object.entries(params || {})
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('');
    const response = await fetch(`${endpoint}?q=${encodeURIComponent(query)}${extra}`, { credentials, headers });
    const payload = await response.json();

    if (!response.ok) {
      if (response.status === 400) {
        return {
          kind: 'validation',
          message: payload.error || validation.message,
          severity: payload.severity === 'warning' ? 'warning' : 'error',
        };
      }
      // Signed out (K15 pt 11): the screen shows its sign-in. Trying the search again would not help.
      if (response.status === 401) {
        return { kind: 'signin' };
      }
      return { kind: 'error', message: SEARCH_ERROR_MESSAGE, severity: 'error' };
    }

    const result = payload?.data && !Object.prototype.hasOwnProperty.call(payload, 'answer')
      ? { ...payload.data, queryId: payload.queryId ?? payload.data.queryId }
      : payload;
    const answer = typeof result?.answer === 'string' ? result.answer.trim() : '';
    const contentId = result?.contentId ?? result?.matchedContent?.id;
    const isGap = !contentId
      || !answer
      || answer === NO_COVERAGE_ANSWER
      || result?.message === SEARCH_GAP_MESSAGE;

    if (isGap) {
      return {
        kind: 'gap',
        message: SEARCH_GAP_MESSAGE,
        result: { ...result, answer: null, message: SEARCH_GAP_MESSAGE },
      };
    }

    return { kind: 'answer', result: { ...result, answer, contentId } };
  } catch {
    return { kind: 'error', message: SEARCH_ERROR_MESSAGE, severity: 'error' };
  }
};

// One look for the error state on every search screen (K15 pts 4-5): the theme's error alert with a Try again
// button, so it never reads as the gap message. sx sets only the spacing around it.
const SearchErrorState = ({ onRetry, sx }) => (
  <Alert
    severity="error"
    role="alert"
    sx={{ alignItems: 'center', ...sx }}
    action={onRetry ? (
      <Button color="inherit" size="small" variant="outlined" onClick={onRetry}>
        Try again
      </Button>
    ) : undefined}
  >
    {SEARCH_ERROR_MESSAGE}
  </Alert>
);

// Where an answer comes from, for its source line (N4 pt 1: the sourced answer shows its page or time): "Page 3" for
// a document, "1:05" for a video or audio passage. Null when the passage has neither.
export const seekLabel = (seek) => {
  if (!seek) return null;
  if (seek.type === 'page' && seek.pageNumber) return `Page ${seek.pageNumber}`;
  const seconds = Number(seek.seconds);
  if (seek.type === 'time' && Number.isFinite(seconds) && seconds >= 0) {
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  }
  return null;
};

// The one optional question, "What were you looking for?" (N4 pt 4). The answer is saved on the search
// (user_queries.comment) and never shown back on screen; the export carries it in v2 (DI3). In the app it shows only
// after a No on a shown answer (N4 pt 4.1); the widget still shows it at its gap state until its own change (PR #12).
// Once sent, one line takes its place (N5 pt 38).
export const GapComment = ({ queryId, sx }) => {
  const [text, setText] = React.useState('');
  const [state, setState] = React.useState('idle');
  React.useEffect(() => { setText(''); setState('idle'); }, [queryId]);
  if (!queryId) return null;
  if (state === 'sent') {
    // Removed 2 Nov 2026 (N5 pt 38): 'Thanks for telling us.', then this line's closing 'Thanks.', as the answer's row
    // above it already reads 'Thanks for your feedback.' (agreed 2 Nov).
    return <Typography sx={{ mt: 2, fontSize: '14px', color: 'inherit', ...sx }}>We will follow up once this is answered.</Typography>;
  }
  const send = async (event) => {
    event.preventDefault();
    const comment = text.trim();
    if (!comment || state === 'sending') return;
    setState('sending');
    try {
      const response = await fetch('/api/user-queries/comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queryId, comment }),
      });
      setState(response.ok ? 'sent' : 'failed');
    } catch {
      setState('failed');
    }
  };
  const fieldId = `gap-comment-${queryId}`;
  return (
    <Box component="form" onSubmit={send} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left', ...sx }}>
      <Typography component="label" htmlFor={fieldId} sx={{ fontSize: '14px', fontWeight: 500, color: 'inherit' }}>
        What were you looking for?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          id={fieldId}
          size="small"
          fullWidth
          placeholder="Optional"
          value={text}
          onChange={(event) => setText(event.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        <Button type="submit" variant="outlined" size="small" disabled={!text.trim() || state === 'sending'}>
          Send
        </Button>
      </Box>
      {state === 'failed' && (
        <Typography role="alert" sx={{ fontSize: '13px', color: 'error.main' }}>Not sent. Try again.</Typography>
      )}
    </Box>
  );
};

const ContentSearchMessage = ({ kind, message, sx, onRetry }) => {
  if (kind === 'error') return <SearchErrorState onRetry={onRetry} sx={sx} />;
  const text = kind === 'gap' ? SEARCH_GAP_MESSAGE : kind === 'learner-gap' ? LEARNER_GAP_MESSAGE : message;
  if (!text) return null;

  return (
    <Typography sx={sx}>
      {text}
    </Typography>
  );
};

export default ContentSearchMessage;